import { useCallback, useEffect, useMemo, useRef, useState, useId } from "react";
import { fetchProducts } from "../api/products";
import { fetchCategories, createCategory } from "../api/categories";
import { requestPresignedUpload, uploadToPresignedUrl } from "../api/assets";
import useSignedAssetUrls from "../hooks/useSignedAssetUrls";
import { normalizeAssetKeys, resolveAssetUrl } from "../utils/assets";

const STATUS_OPTIONS = [
  { key: "available", label: "Available", tone: "success" },
  { key: "low", label: "Low", tone: "warn" },
  { key: "out", label: "Out of stock", tone: "danger" },
];

const INITIAL_FORM = {
  status: "available",
  name: "",
  refCode: "",
  barcode: "",
  quantity: "",
  reorderLevel: "",
  costPrice: "",
  defaultPrice: "",
  expiryDate: "",
  description: "",
  zone: "",
  supplierId: "",
  categories: [],
  imageKeys: [],
  imageUrl: "",
  origin: "",
  id: "",
};

const formatDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toStringValue = (value) => {
  if (value === null || value === undefined) return "";
  return `${value}`;
};

const MAX_IMAGE_DIMENSION = 1000;

const loadImageSource = async (file) => {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file, { imageOrientation: "from-image" });
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(objectUrl);
      reject(err);
    };
    img.src = objectUrl;
  });
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Unable to process image."));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });

const toSquareImageFile = async (file) => {
  const source = await loadImageSource(file);
  const width = source.width || source.naturalWidth || 0;
  const height = source.height || source.naturalHeight || 0;
  if (!width || !height) {
    if (source.close) source.close();
    throw new Error("Unable to read image dimensions.");
  }

  const side = Math.min(width, height);
  const targetSize = Math.min(side, MAX_IMAGE_DIMENSION);
  const sx = Math.floor((width - side) / 2);
  const sy = Math.floor((height - side) / 2);

  const canvas = document.createElement("canvas");
  canvas.width = targetSize;
  canvas.height = targetSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    if (source.close) source.close();
    throw new Error("Unable to prepare image.");
  }

  ctx.drawImage(source, sx, sy, side, side, 0, 0, targetSize, targetSize);
  if (source.close) source.close();

  const outputType = ["image/jpeg", "image/png", "image/webp"].includes(file.type) ? file.type : "image/jpeg";
  const quality = outputType === "image/jpeg" ? 0.9 : 1;
  const blob = await canvasToBlob(canvas, outputType, quality);
  return new File([blob], file.name, { type: blob.type || outputType });
};

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value?.$oid) return value.$oid;
  return value.toString?.() || "";
};

const normalizeCategoryEntry = (value) => {
  if (!value) return null;
  const id = normalizeId(value?._id || value?.id || value?.value);
  const name =
    typeof value === "string" ? value : value?.name || value?.title || value?.label || value?.item?.title || "";
  if (!id && !name) return null;
  return { id, name: name || "Untitled category" };
};

const dedupeCategories = (items = []) => {
  const seen = new Set();
  const result = [];
  items.forEach((cat) => {
    if (!cat) return;
    const id = cat.id || "";
    const name = cat.name || "Untitled category";
    const key = id || name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push({ id, name });
  });
  return result;
};

const upsertCategory = (existing = [], nextCat) => {
  if (!nextCat) return existing || [];
  const normalized = { id: nextCat.id || "", name: nextCat.name || "Untitled category" };
  const key = normalized.id || normalized.name.toLowerCase();
  const list = Array.isArray(existing) ? [...existing] : [];
  const idx = list.findIndex((cat) => (cat.id || cat.name.toLowerCase()) === key);
  if (idx >= 0) {
    list[idx] = normalized;
    return list;
  }
  list.push(normalized);
  return list;
};

const extractCategories = (product) => {
  const list = [];
  const pushCategory = (cat) => {
    if (!cat) return;
    list.push(cat);
  };

  if (Array.isArray(product?.categories)) {
    product.categories.forEach((cat) => pushCategory(normalizeCategoryEntry(cat)));
  }

  if (Array.isArray(product?.categoryIds)) {
    product.categoryIds.forEach((id) => pushCategory(normalizeCategoryEntry({ id })));
  }

  if (product?.categoryId) {
    pushCategory(normalizeCategoryEntry({ id: product.categoryId, name: product?.categoryName || product?.category?.name }));
  }

  if (!list.length && product?.categoryName) {
    pushCategory(normalizeCategoryEntry({ name: product.categoryName }));
  }

  return dedupeCategories(list);
};

const mapProductToForm = (product) => ({
  ...INITIAL_FORM,
  id: normalizeId(product?.id || product?._id),
  status: product?.status || INITIAL_FORM.status,
  name: product?.name || "",
  refCode: product?.refCode || product?.sku || "",
  barcode: product?.barcode || "",
  quantity: toStringValue(product?.quantityOnHand),
  reorderLevel: toStringValue(product?.reorderLevel),
  costPrice: toStringValue(product?.costPrice),
  defaultPrice: toStringValue(product?.defaultPrice),
  expiryDate: formatDateInput(product?.expiryDate),
  description: product?.description || "",
  zone: product?.zone || "",
  supplierId: normalizeId(product?.supplierId),
  categories: extractCategories(product),
  imageKeys: normalizeAssetKeys(product?.imageKeys),
  imageUrl: product?.imageUrl || "",
  origin: product?.origin || "",
});

function ProductDrawer({ open, onClose, onSubmit, onUpdate, saving = false, error = "", withOverlay = false, product = null, mode = "create", suppliers = [], onOpenBarcodeModal }) {
  const drawerRef = useRef(null);
  const categoryWrapRef = useRef(null);
  const categoryFieldRef = useRef(null);
  const supplierWrapRef = useRef(null);
  const imageInputRef = useRef(null);
  const categoryLabelId = useId();
  const [form, setForm] = useState(INITIAL_FORM);
  const [initialForm, setInitialForm] = useState(INITIAL_FORM);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [localError, setLocalError] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [supplierQuery, setSupplierQuery] = useState("");
  const isCreateMode = mode === "create";
  const isReadOnly = mode === "view";
  const isEditMode = mode === "edit";
  const allowEdits = !isReadOnly;
  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialForm), [form, initialForm]);
  const normalizedImageKeys = useMemo(() => normalizeAssetKeys(form?.imageKeys), [form?.imageKeys]);
  const imageKeysToSign = useMemo(
    () => normalizeAssetKeys([...normalizedImageKeys, form?.imageUrl]),
    [normalizedImageKeys, form?.imageUrl]
  );
  const signedAssetUrls = useSignedAssetUrls(imageKeysToSign);
  const imagePreviewItems = useMemo(() => {
    if (normalizedImageKeys.length) {
      return normalizedImageKeys.map((key) => ({
        key,
        url: resolveAssetUrl(key, signedAssetUrls),
      }));
    }
    if (form?.imageUrl) {
      return [{ key: form.imageUrl, url: resolveAssetUrl(form.imageUrl, signedAssetUrls), legacy: true }];
    }
    return [];
  }, [form?.imageUrl, normalizedImageKeys, signedAssetUrls]);

  const statusLabel = useMemo(() => STATUS_OPTIONS.find((item) => item.key === form.status)?.label || "Status", [form.status]);
  const hasInventoryInputs = useMemo(() => {
    const hasQuantityValue = form.quantity !== "" && !Number.isNaN(Number(form.quantity));
    const hasAlertValue = form.reorderLevel !== "" && !Number.isNaN(Number(form.reorderLevel));
    return hasQuantityValue && hasAlertValue;
  }, [form.quantity, form.reorderLevel]);

  const supplierOptions = useMemo(() => {
    const seen = new Set();
    const options = (suppliers || [])
      .map((sup) => {
        const id = normalizeId(sup?.id || sup?._id || sup?.value);
        if (!id) return null;
        return { id, name: sup?.name || sup?.title || sup?.label || sup?.item?.title || "Unknown supplier" };
      })
      .filter(Boolean);
    return options.filter(({ id }) => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [suppliers]);

  const categoryLookup = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((cat) => {
      const id = normalizeId(cat?._id || cat?.id);
      if (!id) return;
      map.set(id, cat?.name || "Untitled category");
    });
    return map;
  }, [categories]);

  const categoryOptions = useMemo(() => {
    const seen = new Set();
    const options = (categories || [])
      .map((cat) => {
        const id = normalizeId(cat?._id || cat?.id);
        if (!id) return null;
        return { id, name: cat?.name || "Untitled" };
      })
      .filter(Boolean)
      .filter(({ id }) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    selectedCategories
      .filter((cat) => cat?.id && !options.find((opt) => opt.id === cat.id))
      .forEach((cat) => {
        const name = cat.name || "Current category";
        options.push({ id: cat.id, name });
      });

    return options;
  }, [categories, selectedCategories]);

  const selectedCategoryKeys = useMemo(() => {
    const keys = new Set();
    selectedCategories.forEach((cat) => {
      const key = cat?.id || cat?.name?.toLowerCase();
      if (key) keys.add(key);
    });
    return keys;
  }, [selectedCategories]);

  const filteredCategories = useMemo(() => {
    const query = categoryInput.trim().toLowerCase();
    return categoryOptions.filter(
      (cat) => {
        const key = cat.id || cat.name.toLowerCase();
        return !selectedCategoryKeys.has(key) && (!query || cat.name.toLowerCase().includes(query));
      }
    );
  }, [categoryInput, categoryOptions, selectedCategoryKeys]);

  const showCreateCategoryOption = useMemo(() => {
    const name = categoryInput.trim();
    if (!name) return false;
    const lower = name.toLowerCase();
    const existsInOptions = categoryOptions.some((cat) => cat.name.toLowerCase() === lower);
    const existsInSelection = selectedCategories.some((cat) => cat.name?.toLowerCase() === lower);
    return !existsInOptions && !existsInSelection;
  }, [categoryInput, categoryOptions, selectedCategories]);

  const applyCategoryNames = useCallback(
    (list = []) => {
      if (!Array.isArray(list) || !list.length || !categoryLookup.size) return list;
      let changed = false;
      const next = list.map((cat) => {
        if (!cat?.id) return cat;
        const resolvedName = categoryLookup.get(cat.id);
        if (resolvedName && resolvedName !== cat.name) {
          changed = true;
          return { ...cat, name: resolvedName };
        }
        return cat;
      });
      return changed ? next : list;
    },
    [categoryLookup]
  );

  const filteredSuppliers = useMemo(() => {
    const query = supplierQuery.trim().toLowerCase();
    if (!query) return supplierOptions;
    return supplierOptions.filter((sup) => sup.name.toLowerCase().includes(query));
  }, [supplierOptions, supplierQuery]);

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [field]: value }));
    setLocalError("");
  };

  const handleSupplierInput = (event) => {
    const value = event?.target?.value ?? "";
    setSupplierQuery(value);
    setForm((prev) => ({ ...prev, supplierId: "" }));
    setLocalError("");
    setSupplierOpen(true);
  };

  const handleSelectSupplier = (sup) => {
    const id = normalizeId(sup?.id);
    setForm((prev) => ({ ...prev, supplierId: id }));
    setSupplierQuery(sup?.name || "");
    setSupplierOpen(false);
    setLocalError("");
  };

  const handleClearSupplier = () => {
    setForm((prev) => ({ ...prev, supplierId: "" }));
    setSupplierQuery("");
    setSupplierOpen(false);
  };

  useEffect(() => {
    if (isReadOnly) return;

    const qty = Number(form.quantity);
    const alertLevel = Number(form.reorderLevel);

    const hasQty = !Number.isNaN(qty);
    const hasAlert = !Number.isNaN(alertLevel);

    let nextStatus = form.status || "available";
    if (hasQty) {
      if (qty <= 0) {
        nextStatus = "out";
      } else if (hasAlert && qty <= alertLevel) {
        nextStatus = "low";
      } else {
        nextStatus = "available";
      }
    }

    if (nextStatus !== form.status) {
      setForm((prev) => ({ ...prev, status: nextStatus }));
    }
  }, [form.quantity, form.reorderLevel, form.status, isReadOnly]);

  const validateForm = useCallback(() => {
    if (!form.name.trim() || !form.refCode.trim()) {
      setLocalError("Product name and ref code are required.");
      return false;
    }

    const numericFields = [
      ["quantity", "Quantity"],
      ["reorderLevel", "Stock alert"],
      ["costPrice", "Cost"],
      ["defaultPrice", "Sell price"],
    ];

    for (const [field, label] of numericFields) {
      const value = form[field];
      if (value === "" || value === null || value === undefined) continue;
      if (Number.isNaN(Number(value))) {
        setLocalError(`${label} must be a number.`);
        return false;
      }
    }

    return true;
  }, [form]);

  const loadCategories = async () => {
    try {
      const list = await fetchCategories();
      setCategories(Array.isArray(list) ? list : []);
    } catch (_err) {
      setCategories([]);
    }
  };

  const addCategoryToForm = (cat) => {
    if (!cat) return;
    const payload = { id: cat.id || "", name: cat.name || "Untitled category" };
    setSelectedCategories((prev) => {
      const next = upsertCategory(prev, payload);
      setForm((current) => ({ ...current, categories: next }));
      return next;
    });
    setLocalError("");
  };

  const handleCategoryInputChange = (event) => {
    const value = event?.target?.value ?? "";
    setCategoryInput(value);
    setLocalError("");
    setCategoryOpen(true);
  };

  const handleCategoryBlur = () => {
    // Wait a tick to allow clicks inside the dropdown to register before closing.
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (categoryWrapRef.current && categoryWrapRef.current.contains(active)) return;
      closeCategoryDropdown();
    });
  };

  const handleCategorySelect = (cat) => {
    addCategoryToForm(cat);
    setCategoryInput("");
    setCategoryOpen(true);
  };

  const handleCreateCategory = async (nameOverride, { autoAdd = true } = {}) => {
    if (isReadOnly) return null;
    const name = (nameOverride ?? categoryInput).trim();
    if (!name) {
      setLocalError("Category name is required.");
      return null;
    }
    setCreatingCategory(true);
    try {
      const created = await createCategory({ name });
      const createdId = normalizeId(created?._id || created?.id);
      const createdName = created?.name || name;

      if (createdId) {
        setCategories((prev) => {
          const existing = prev.find((cat) => normalizeId(cat?._id || cat?.id) === createdId);
          if (existing) return prev;
          return [{ _id: createdId, name: createdName }, ...prev];
        });
      }

      const createdCat = { id: createdId, name: createdName };
      if (autoAdd) {
        addCategoryToForm(createdCat);
        setCategoryInput("");
        setCategoryOpen(false);
      }
      return createdCat;
    } catch (err) {
      setLocalError(err?.message || "Unable to create category.");
      return null;
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleCategoryKeyDown = async (event) => {
    if (creatingCategory) return;
    if (event.key === "Enter") {
      event.preventDefault();
      if (filteredCategories.length) {
        handleCategorySelect(filteredCategories[0]);
        return;
      }
      if (showCreateCategoryOption) {
        await handleCreateCategory();
      }
    }
  };

  const handleRemoveCategory = (index) => {
    setSelectedCategories((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      setForm((current) => ({ ...current, categories: next }));
      return next;
    });
  };

  const handleClearImages = () => {
    if (!allowEdits) return;
    setForm((prev) => ({ ...prev, imageKeys: [], imageUrl: "" }));
    setImageError("");
  };

  const handleRemoveImage = (key) => {
    if (!allowEdits) return;
    setForm((prev) => {
      if (prev.imageUrl && prev.imageUrl === key) {
        return { ...prev, imageUrl: "" };
      }
      const nextKeys = Array.isArray(prev.imageKeys) ? prev.imageKeys.filter((item) => item !== key) : [];
      return { ...prev, imageKeys: nextKeys };
    });
  };

  const handleImageSelected = async (event) => {
    if (!allowEdits) return;
    const files = Array.from(event?.target?.files || []).filter(Boolean);
    if (!files.length) return;

    setImageError("");
    setLocalError("");

    const productId = form?.id || normalizeId(product?.id || product?._id);
    if (!productId) {
      setImageError("Save the product first, then upload images.");
      if (event?.target) event.target.value = "";
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    const invalidType = files.find((file) => !validTypes.includes(file.type));
    if (invalidType) {
      setImageError("Please upload JPG, PNG, or WebP files only.");
      if (event?.target) event.target.value = "";
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    const oversized = files.find((file) => file.size > maxSize);
    if (oversized) {
      setImageError("Image is too large. Max size is 5MB.");
      if (event?.target) event.target.value = "";
      return;
    }

    setUploadingImage(true);
    try {
      const processedFiles = [];
      for (const file of files) {
        const squared = await toSquareImageFile(file);
        processedFiles.push(squared);
      }

      const uploadedKeys = [];
      for (const file of processedFiles) {
        const result = await requestPresignedUpload({
          productId,
          fileName: file.name,
          contentType: file.type,
        });
        if (!result?.uploadUrl || !result?.key) {
          throw new Error("Unable to get upload URL for the image.");
        }
        await uploadToPresignedUrl({
          uploadUrl: result.uploadUrl,
          file,
          contentType: file.type,
        });
        uploadedKeys.push(result.key);
      }

      setForm((prev) => {
        const existing = Array.isArray(prev.imageKeys) ? prev.imageKeys : [];
        const merged = Array.from(new Set([...existing, ...uploadedKeys]));
        return { ...prev, imageKeys: merged };
      });
      setImageError("");
    } catch (err) {
      console.error("Image upload failed", err);
      setImageError(err?.message || "Unable to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
      if (event?.target) event.target.value = "";
    }
  };

  const closeCategoryDropdown = useCallback(() => {
    setCategoryOpen(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isReadOnly) {
      onClose?.();
      return;
    }
    if (!validateForm()) return;

    const categoriesToSubmit = selectedCategories;

    let nextForm = { ...form, categories: categoriesToSubmit };
    const categoryIds = (categoriesToSubmit || []).map((cat) => cat.id).filter(Boolean);
    nextForm = { ...nextForm, categoryIds, categoryId: categoryIds[0] || "" };

    if (isEditMode) {
      onUpdate?.(nextForm, product);
      return;
    }
    onSubmit?.(nextForm);
  }, [form, isEditMode, isReadOnly, onClose, onSubmit, onUpdate, product, selectedCategories, validateForm]);

  const handleCloseAttempt = useCallback(() => {
    if (!allowEdits || !isDirty) {
      onClose?.();
      return;
    }

    const shouldSave = window.confirm("You have unsaved changes. Save before closing? Press Cancel to discard.");
    if (shouldSave) {
      handleSubmit();
    } else {
      onClose?.();
    }
  }, [allowEdits, handleSubmit, isDirty, onClose]);

  useEffect(() => {
    if (open) {
      const nextForm = product ? mapProductToForm(product) : INITIAL_FORM;
      const mappedCategories = nextForm.categories || [];
      setForm(nextForm);
      setInitialForm(nextForm);
      setSelectedCategories(mappedCategories);
      setLocalError("");
      setCategoryInput("");
      setCategoryOpen(false);
      setSupplierOpen(false);
      setImageError("");
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
      loadCategories();
    }
  }, [open, product]);

  useEffect(() => {
    if (!open) return;
    const match = supplierOptions.find((sup) => sup.id === form.supplierId);
    if (match) {
      setSupplierQuery(match.name);
    } else if (!form.supplierId) {
      setSupplierQuery("");
    }
  }, [form.supplierId, supplierOptions, open]);

  useEffect(() => {
    if (!open || !categoryLookup.size) return;

    setSelectedCategories((prev) => {
      const next = applyCategoryNames(prev);
      return next === prev ? prev : next;
    });

    setForm((prev) => {
      const nextCats = applyCategoryNames(prev.categories || []);
      if (nextCats === prev.categories) return prev;
      return { ...prev, categories: nextCats };
    });

    setInitialForm((prev) => {
      const nextCats = applyCategoryNames(prev.categories || []);
      if (nextCats === prev.categories) return prev;
      return { ...prev, categories: nextCats };
    });
  }, [open, categoryLookup, applyCategoryNames]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutsideCategories = (event) => {
      const container = categoryFieldRef.current || categoryWrapRef.current;
      if (!container) return;
      if (container.contains(event.target)) return;
      closeCategoryDropdown();
    };

    document.addEventListener("mousedown", handleClickOutsideCategories);
    return () => document.removeEventListener("mousedown", handleClickOutsideCategories);
  }, [open, closeCategoryDropdown]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutsideSupplier = (event) => {
      if (!supplierWrapRef.current) return;
      if (supplierWrapRef.current.contains(event.target)) return;
      setSupplierOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutsideSupplier);
    return () => document.removeEventListener("mousedown", handleClickOutsideSupplier);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (withOverlay) return undefined;

    const handleClickOutside = (event) => {
      if (!drawerRef.current) return;
      if (drawerRef.current.contains(event.target)) return;
      handleCloseAttempt();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleCloseAttempt, open, withOverlay]);

  const handleOpenBarcodeModal = useCallback(() => {
    if (!onOpenBarcodeModal) return;
    const preselectIds = [
      normalizeId(form.id || product?.id || product?._id),
      form.barcode?.trim(),
      form.refCode?.trim(),
    ].filter(Boolean);
    onOpenBarcodeModal({ preselectIds });
  }, [form.barcode, form.id, form.refCode, normalizeId, onOpenBarcodeModal, product]);

  if (!open) return null;

  const generateCandidateBarcode = () => {
    const random15 = Math.floor(Math.random() * 10 ** 15)
      .toString()
      .padStart(15, "0");
    return `2${random15}`;
  };

  const handleGenerateBarcode = async () => {
    if (isReadOnly) return;
    setLocalError("");
    setBarcodeLoading(true);
    try {
      const products = (await fetchProducts()) || [];
      const existing = new Set(
        products
          .map((p) => p?.barcode)
          .filter(Boolean)
      );

      let candidate = generateCandidateBarcode();
      let attempts = 0;
      while (existing.has(candidate) && attempts < 50) {
        candidate = generateCandidateBarcode();
        attempts += 1;
      }

      if (existing.has(candidate)) {
        throw new Error("Unable to generate a unique barcode. Please try again.");
      }

      setForm((prev) => ({ ...prev, barcode: candidate }));
    } catch (err) {
      setLocalError(err?.message || "Unable to generate barcode. Please try again.");
    } finally {
      setBarcodeLoading(false);
    }
  };

  const disableSubmit = saving || creatingCategory || uploadingImage || !form.name.trim() || !form.refCode.trim();
  const disableUpdate = disableSubmit || !isDirty;

  const content = (
    <aside
      className="create-drawer"
      aria-label={isCreateMode ? "Create product panel" : "Product details panel"}
      ref={drawerRef}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {localError || error ? (
        <div className="inline-alert error">
          <span>{localError || error}</span>
        </div>
      ) : null}

      <div className="drawer-section">
        <h3>Product Details</h3>

        <div className="drawer-grid">
          <div className="field">
            <div className="field-top">
              <label className="field-label">Status</label>
              <span className="muted extra-small">{statusLabel}</span>
            </div>
            <div className="status-row read-only">
              {STATUS_OPTIONS.map((item) => {
                const toneClass = !hasInventoryInputs || form.status === item.key ? item.tone : "";
                const isActive = !hasInventoryInputs || form.status === item.key;
                const classes = ["pill", "status", toneClass, isActive ? "active" : ""].filter(Boolean).join(" ");

                return (
                  <div key={item.key} className={classes} aria-live="polite">
                    {item.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="field image-field">
            <div className="field-top">
              <span className="field-label">Product images</span>
              <span className="muted extra-small">1000×1000</span>
            </div>
            <div className="image-upload-row">
              <div className="image-preview-grid" aria-label="Product images preview">
                {imagePreviewItems.length ? (
                  imagePreviewItems.map((item) => (
                    <div key={item.key} className="image-preview-item">
                      {item.url ? (
                        <img src={item.url} alt={`${form.name || "Product"} image`} />
                      ) : (
                        <div className="image-placeholder">Image uploaded</div>
                      )}
                      {allowEdits ? (
                        <button
                          type="button"
                          className="image-remove-btn"
                          onClick={() => handleRemoveImage(item.key)}
                          disabled={uploadingImage}
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="image-placeholder">No images</div>
                )}
              </div>
              {allowEdits ? (
                <div className="image-actions">
                  <label className={`upload-btn ${uploadingImage ? "disabled" : ""}`}>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/*"
                      multiple
                      onChange={handleImageSelected}
                      disabled={uploadingImage}
                      ref={imageInputRef}
                    />
                    {uploadingImage ? "Uploading..." : "Upload images"}
                  </label>
                  {imagePreviewItems.length ? (
                    <button type="button" className="ghost-btn" onClick={handleClearImages} disabled={uploadingImage}>
                      Clear all
                    </button>
                  ) : null}
                  <p className="muted extra-small">JPG/PNG/WebP, max 5MB each. Recommended 1000×1000.</p>
                  {imageError ? <p className="error-text extra-small">{imageError}</p> : null}
                </div>
              ) : null}
            </div>
          </div>

          <label className="field">
            <span className="field-label">Product name</span>
            <input
              type="text"
              placeholder="Product name"
              value={form.name}
              disabled={!allowEdits}
              onChange={handleChange("name")}
            />
          </label>

          <label className="field">
            <span className="field-label">Description</span>
            <input
              type="text"
              placeholder="Description"
              value={form.description}
              disabled={!allowEdits}
              onChange={handleChange("description")}
            />
          </label>

          <div className="field two-col">
            <label className="field">
              <span className="field-label">Zone (optional)</span>
              <input
                type="text"
                placeholder="e.g., Aisle 4B"
                value={form.zone}
                disabled={!allowEdits}
                onChange={handleChange("zone")}
              />
            </label>

            <label className="field">
              <span className="field-label">Exp. Date</span>
              <div className="input-with-icon">
                <input type="date" value={form.expiryDate} disabled={!allowEdits} onChange={handleChange("expiryDate")} />
                <span className="input-icon muted">🗓</span>
              </div>
            </label>
          </div>

          <label className="field">
            <span className="field-label">Ref Code</span>
            <div className="input-with-icon">
              <input type="text" placeholder="Type..." value={form.refCode} disabled={!allowEdits} onChange={handleChange("refCode")} />
              <span className="input-icon muted">📋</span>
            </div>
          </label>

          <label className="field">
            <span className="field-label">Barcode</span>
            <div className="input-with-action">
              <input type="text" placeholder="Scan or enter barcode..." value={form.barcode} disabled={!allowEdits} onChange={handleChange("barcode")} />
              <button className="ghost-btn generate-btn" type="button" onClick={handleGenerateBarcode} disabled={barcodeLoading || isReadOnly}>
                {barcodeLoading ? "Generating..." : "Generate"}
              </button>
            </div>
          </label>
          {onOpenBarcodeModal ? (
            <div className="inline-actions">
              <button className="ghost-btn subtle small" type="button" onClick={handleOpenBarcodeModal} disabled={isReadOnly}>
                Print barcodes
              </button>
            </div>
          ) : null}

          <div className="field two-col">
            <label className="field">
              <span className="field-label">Quantity</span>
              <input type="number" placeholder="Type..." value={form.quantity} disabled={!allowEdits} onChange={handleChange("quantity")} />
            </label>

            <label className="field">
              <span className="field-label">Stock alert</span>
              <input
                type="number"
                placeholder="Stock level to alert as low..."
                value={form.reorderLevel}
                disabled={!allowEdits}
                onChange={handleChange("reorderLevel")}
              />
            </label>
          </div>

          <div className="field two-col">
            <label className="field">
              <span className="field-label">Cost</span>
              <div className="input-with-icon">
                <input
                  type="number"
                  placeholder="Type..."
                  value={form.costPrice}
                  disabled={!allowEdits}
                  onChange={handleChange("costPrice")}
                  step="0.001"
                />
                <span className="input-icon muted">KD</span>
              </div>
            </label>

            <label className="field">
              <span className="field-label">Price</span>
              <div className="input-with-icon">
                <input
                  type="number"
                  placeholder="Type..."
                  value={form.defaultPrice}
                  disabled={!allowEdits}
                  onChange={handleChange("defaultPrice")}
                  step="0.001"
                />
                <span className="input-icon muted">KD</span>
              </div>
            </label>
          </div>

          <label className="field">
            <span className="field-label">Supplier</span>
            <div
              className="input-with-action category-input-wrap"
              onMouseDown={() => allowEdits && setSupplierOpen(true)}
              ref={supplierWrapRef}
            >
              <input
                type="text"
                placeholder="Search or select supplier..."
                value={supplierQuery}
                disabled={!allowEdits}
                onChange={handleSupplierInput}
                onFocus={() => allowEdits && setSupplierOpen(true)}
              />
              {supplierOpen && allowEdits ? (
                <div className="dropdown-panel">
                  <button
                    type="button"
                    className="dropdown-item"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleClearSupplier();
                    }}
                  >
                    No supplier
                  </button>
                  <div className="dropdown-divider" />
                  {filteredSuppliers.length ? (
                    filteredSuppliers.map((sup) => (
                      <button
                        type="button"
                        className="dropdown-item"
                        key={sup.id}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleSelectSupplier(sup);
                        }}
                      >
                        {sup.name}
                      </button>
                    ))
                  ) : (
                    <div className="dropdown-item muted">No suppliers found</div>
                  )}
                </div>
              ) : null}
            </div>
          </label>

          <div className="field" ref={categoryFieldRef}>
            <span className="field-label" id={categoryLabelId}>
              Categories
            </span>
            <div className="category-input-wrap multi" ref={categoryWrapRef} aria-labelledby={categoryLabelId}>
              <div className="category-tags">
                {selectedCategories.map((cat, idx) => (
                  <span className="category-chip" key={`${cat.id || cat.name || idx}-${idx}`}>
                    <span className="category-chip-name">{cat.name || "Untitled"}</span>
                    {allowEdits ? (
                      <button
                        type="button"
                        className="category-chip-remove"
                        aria-label={`Remove ${cat.name || "category"}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleRemoveCategory(idx);
                        }}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        ×
                      </button>
                    ) : null}
                  </span>
                ))}
                {allowEdits ? (
                  <input
                    type="text"
                    placeholder={selectedCategories.length ? "Add another..." : "Type to search or create..."}
                    value={categoryInput}
                    disabled={creatingCategory}
                    onChange={handleCategoryInputChange}
                    onFocus={() => setCategoryOpen(true)}
                    onClick={() => setCategoryOpen(true)}
                    onBlur={handleCategoryBlur}
                    onKeyDown={handleCategoryKeyDown}
                  />
                ) : null}
              </div>
              {categoryOpen && allowEdits ? (
                <div className="dropdown-panel">
                  {filteredCategories.length ? (
                    filteredCategories.map((cat) => (
                      <button
                        type="button"
                        className="dropdown-item"
                        key={cat.id || cat.name}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          handleCategorySelect(cat);
                        }}
                      >
                        {cat.name}
                      </button>
                    ))
                  ) : (
                    <div className="dropdown-item muted">No categories found</div>
                  )}
                  {showCreateCategoryOption ? (
                    <>
                      <div className="dropdown-divider" />
                      <button
                        type="button"
                        className="dropdown-item create"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          handleCreateCategory(categoryInput);
                        }}
                        disabled={creatingCategory || !categoryInput.trim()}
                      >
                        {creatingCategory ? "Creating..." : `Create “${categoryInput.trim()}”`}
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <footer className="drawer-footer">
        {isCreateMode || (isEditMode && isDirty) ? (
          <button
            className="primary-btn wide"
            type="button"
            onClick={handleSubmit}
            disabled={isEditMode ? disableUpdate : disableSubmit}
          >
            {saving ? (isEditMode ? "Updating..." : "Creating...") : isEditMode ? "Update product" : "Create product"}
          </button>
        ) : null}
        <button className="ghost-btn subtle wide" type="button" onClick={onClose} disabled={saving}>
          {isReadOnly ? "Close" : "Cancel"}
        </button>
      </footer>
    </aside>
  );

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleCloseAttempt();
    }
  };

  if (!withOverlay) return content;

  return (
    <div className="modal-overlay solo-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className="create-layer solo-drawer">{content}</div>
    </div>
  );
}

export default ProductDrawer;
